import {
  Alert,
  AlertDescription,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  Center,
} from '@chakra-ui/react';
import { PasswordField } from 'src/components/atoms';
import { useAuth } from 'src/utils/auth';
import { useContext, useState } from 'react';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { gaEvent } from 'src/utils/gtag';
import { MarketplaceContext } from 'src/contexts/marketplace';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
import LoginFormSkeleton from 'src/components/skeletons/login-form-skeleton';
import SocialLoginRow from 'src/components/authentication/SocialLoginRow';
import { useMarketingOnboardingSafe } from 'src/contexts/marketingOnboarding';

interface LoginFormProps {
  userEmail?: string;
  onClose?: () => void;
  setShowSignUp?: (show: boolean) => void;
  showSignupForm?: boolean;
  loginFormText?: string;
  returnUrl?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  userEmail,
  onClose,
  setShowSignUp,
  loginFormText,
  showSignupForm = true,
  returnUrl,
}) => {
  const { login } = useAuth();
  const router = useRouter();
  const isAdminRoute = router.pathname.startsWith('/slug_admin');
  const showToast = useSingleToast();
  const marketingOnboardingContext = useMarketingOnboardingSafe();

  const [email, setEmail] = useState(userEmail ?? '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { marketplace, marketplaceSlug, isLoading } = useContext(MarketplaceContext);

  const handleForgotPassword = () => {
    onClose && onClose();
    router.push('/forgot-password');
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setErrorMessage(null);
    try {
      if (!marketplaceSlug) return;
      const response = await login(email, password, marketplaceSlug);
      if (response.access === undefined) {
        if (response?.detail === 'The account is inactive') {
          setErrorMessage('Please verify your email address.');
          setLoading(false);
        } else {
          showToast({
            title: 'Invalid login info',
            description: 'Please check your email and password and try again.',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        gaEvent({ action: 'login', category: 'engagement', label: 'method', value: `${email}` });

        onClose && onClose();

        // Check for returnUrl query parameter first (for post-login redirects)
        const queryReturnUrl = router.query.returnUrl as string | undefined;
        if (queryReturnUrl && typeof queryReturnUrl === 'string') {
          if (queryReturnUrl.startsWith('/') && !queryReturnUrl.startsWith('//')) {
            await router.push(queryReturnUrl);
            return;
          }
        }

        // check for a passed returnUrl, like through the LoginModal on profile menu
        if (returnUrl && typeof returnUrl === 'string') {
          if (returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
            await router.push(returnUrl);
            return;
          }
        }

        if (marketplace?.status === 'preview') {
          const redirectUrl = marketingOnboardingContext?.state?.redirectAfterLogin;

          if (redirectUrl) {
            marketingOnboardingContext?.updateState({ redirectAfterLogin: undefined });
            await router.push(redirectUrl);
          } else if (!onClose || router.asPath.endsWith('/login')) {
            await router.push('/');
          }
        } else if (!onClose || router.asPath.endsWith('/login')) {
          await router.push('/');
        }
      }
    } catch (err) {
      showToast({
        title: 'Error',
        description: 'Something went wrong. Please try again later.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error: err,
          level: 'error',
          extras: {
            marketplaceId: marketplace?.id,
            email,
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (isLoading || (!isAdminRoute && (!marketplace || !marketplaceSlug))) {
    return <LoginFormSkeleton />;
  }

  return (
    <Stack spacing="0" overflowY="auto" data-test="login-form">
      <Stack spacing="0">
        <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
          <Heading size={{ base: 'sm', md: 'md' }}>
            Log in to your {isAdminRoute ? 'admin account' : 'account'}
          </Heading>
          {loginFormText && <Text>{loginFormText}</Text>}
        </Stack>
      </Stack>
      {showSignupForm && (
        <HStack justify="center" pt={{ base: '2', sm: '4' }}>
          <Text color="muted">Don&apos;t have an account?</Text>
          {setShowSignUp ? (
            <Button
              variant="link"
              colorScheme="primary"
              onClick={() => setShowSignUp && setShowSignUp(true)}
            >
              Sign up
            </Button>
          ) : (
            <NextLink href="/signup" onClick={onClose}>
              <Button variant="link" colorScheme="primary">
                Sign up
              </Button>
            </NextLink>
          )}
        </HStack>
      )}
      <Box pb="2" px={{ base: '4', sm: '10' }} bg="white">
        <SocialLoginRow
          isRegister={false}
          onClose={onClose}
          showEmailLogin={!isAdminRoute}
          returnUrl={returnUrl}
        />
        <form onSubmit={handleLogin}>
          <Stack spacing="6">
            <Stack spacing="5">
              <FormControl>
                <FormLabel htmlFor="email">Email</FormLabel>
                <Input
                  id="email"
                  data-test="email-input-login-form"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormControl>
              <PasswordField value={password} onChange={(e) => setPassword(e.target.value)} />
            </Stack>
            <HStack justify="space-between">
              <Button variant="link" colorScheme="primary" size="sm" onClick={handleForgotPassword}>
                Forgot password?
              </Button>
            </HStack>
            <Stack spacing="6">
              <Button
                colorScheme="primary"
                size="md"
                type="submit"
                isLoading={loading}
                data-test="login-submit-btn-login-form"
              >
                Log in
              </Button>
            </Stack>
            {errorMessage && (
              <Alert status="error">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
          </Stack>
        </form>
      </Box>
    </Stack>
  );
};

export default LoginForm;
